import type React from 'react'
import { Children, cloneElement, isValidElement, type ReactNode } from 'react'

import { Trans } from '@mntn-dev/i18n'
import { Text } from '@mntn-dev/ui-components/text'

type AppTransProps = React.ComponentProps<typeof Trans> & {
  components?: Record<string, React.ReactNode>
}

/**
 * Automatically key the children for translation strings that use embedded tags like "This is <bold>bold</bold> text".
 * Trans creates an array of un-keyed children ["This is", <bold>bold</bold>, " text"].
 * This fragment ensures that the children are keyed properly without adding additional markup.
 * Using an index as the key should be fine because the children and their order are the same with no additions or removals.
 */
const FragmentWithKey = ({ children }: { children: ReactNode }) => (
  <>
    {Children.map(children, (child, index) =>
      isValidElement(child)
        ? cloneElement(child, { key: child.key ?? index })
        : child
    )}
  </>
)

const AppTrans = ({ components, ...props }: AppTransProps) => (
  <Trans
    {...props}
    parent={FragmentWithKey}
    components={{
      // Anything highly re-usable goes here
      primary: <Text textColor="primary" />,
      secondary: <Text textColor="secondary" />,
      negative: <Text textColor="negative" />,
      bold: <Text fontWeight="bold" />,
      // Anything bespoke or configured should be passed in via components props. E.g., a <Link/> with a specific href
      ...(components ?? {}),
    }}
  />
)

export { AppTrans }
